/**
 * FilterBar Component
 * Search and filtering controls
 */

import React, { useCallback, useRef, useEffect } from "react";
import type {
  Language,
  SkillLevel,
  ContentPillar,
  FilterState,
  ViewMode,
  LexikonStatistics,
} from "../types";
import { LEVEL_CONFIG, PILLAR_CONFIG } from "../types";

interface FilterBarProps {
  filters: FilterState;
  onFilterChange: (updates: Partial<FilterState>) => void;
  onResetFilters: () => void;
  categories: string[];
  statistics: LexikonStatistics;
  language: Language;
  activeFilterCount: number;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  filters,
  onFilterChange,
  onResetFilters,
  categories,
  statistics,
  language,
  activeFilterCount,
  viewMode,
  onViewModeChange,
}) => {
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  const labels = {
    cs: {
      search: "Hledat pojmy...",
      levels: "Úroveň",
      categories: "Kategorie",
      pillars: "Pilíře",
      all: "Vše",
      clear: "Vymazat filtry",
      withCourses: "S kurzy",
      grid: "Mřížka",
      list: "Seznam",
    },
    en: {
      search: "Search terms...",
      levels: "Level",
      categories: "Category",
      pillars: "Pillars",
      all: "All",
      clear: "Clear filters",
      withCourses: "With courses",
      grid: "Grid",
      list: "List",
    },
  };

  const t = labels[language];

  // Debounced search
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        onFilterChange({ searchQuery: value });
      }, 300);
    },
    [onFilterChange]
  );

  // Toggle level filter
  const toggleLevel = useCallback(
    (level: SkillLevel) => {
      const current = filters.selectedLevels;
      const updated = current.includes(level)
        ? current.filter((l) => l !== level)
        : [...current, level];
      onFilterChange({ selectedLevels: updated });
    },
    [filters.selectedLevels, onFilterChange]
  );

  // Toggle category filter
  const toggleCategory = useCallback(
    (category: string) => {
      const current = filters.selectedCategories;
      const updated = current.includes(category as any)
        ? current.filter((c) => c !== category)
        : [...current, category as any];
      onFilterChange({ selectedCategories: updated });
    },
    [filters.selectedCategories, onFilterChange]
  );

  // Toggle pillar filter
  const togglePillar = useCallback(
    (pillar: ContentPillar) => {
      const current = filters.selectedPillars;
      const updated = current.includes(pillar)
        ? current.filter((p) => p !== pillar)
        : [...current, pillar];
      onFilterChange({ selectedPillars: updated });
    },
    [filters.selectedPillars, onFilterChange]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const levels: SkillLevel[] = ["Beginner", "Intermediate", "Advanced"];
  const pillars: ContentPillar[] = ["A", "B", "H", "M", "Z"];

  return (
    <div className="filter-bar">
      {/* Row 1: Search and View Mode */}
      <div className="filter-bar-row">
        <div className="search-container">
          <span className="search-icon">🔍</span>
          <input
            ref={searchRef}
            type="text"
            className="search-input"
            placeholder={t.search}
            defaultValue={filters.searchQuery}
            onChange={handleSearchChange}
            aria-label={t.search}
          />
        </div>

        {/* View Mode Toggle */}
        <div className="filter-chips">
          <button
            className={`filter-chip ${viewMode === "grid" ? "active" : ""}`}
            onClick={() => onViewModeChange("grid")}
          >
            📊 {t.grid}
          </button>
          <button
            className={`filter-chip ${viewMode === "list" ? "active" : ""}`}
            onClick={() => onViewModeChange("list")}
          >
            📋 {t.list}
          </button>
        </div>

        {/* Clear Filters */}
        {activeFilterCount > 0 && (
          <button className="clear-filters-btn" onClick={onResetFilters}>
            ✕ {t.clear} ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Row 2: Level Filters */}
      <div className="filter-bar-row">
        <span style={{ color: "var(--color-text-muted)", fontSize: "12px", marginRight: "8px" }}>
          {t.levels}:
        </span>
        <div className="filter-chips">
          {levels.map((level) => {
            const config = LEVEL_CONFIG[level];
            const isActive = filters.selectedLevels.includes(level);
            return (
              <button
                key={level}
                className={`filter-chip level-${level.toLowerCase()} ${isActive ? "active" : ""}`}
                onClick={() => toggleLevel(level)}
              >
                {config.icon} {config.name[language]} ({statistics.byLevel[level]})
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 3: Pillar Filters */}
      <div className="filter-bar-row">
        <span style={{ color: "var(--color-text-muted)", fontSize: "12px", marginRight: "8px" }}>
          {t.pillars}:
        </span>
        <div className="filter-chips">
          {pillars.map((pillar) => {
            const config = PILLAR_CONFIG[pillar];
            const isActive = filters.selectedPillars.includes(pillar);
            return (
              <button
                key={pillar}
                className={`filter-chip ${isActive ? "active" : ""}`}
                onClick={() => togglePillar(pillar)}
                style={
                  isActive
                    ? { background: config.color, borderColor: config.color }
                    : {}
                }
              >
                {config.icon} {config.name[language]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Row 4: Category Filters (if many) */}
      {categories.length > 0 && (
        <div className="filter-bar-row">
          <span style={{ color: "var(--color-text-muted)", fontSize: "12px", marginRight: "8px" }}>
            {t.categories}:
          </span>
          <div className="filter-chips">
            {categories.slice(0, 8).map((category) => {
              const isActive = filters.selectedCategories.includes(category as any);
              return (
                <button
                  key={category}
                  className={`filter-chip ${isActive ? "active" : ""}`}
                  onClick={() => toggleCategory(category)}
                >
                  {category}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
